import { BatteryUpdate, BatteryUpdateSchema } from '@/validation/batteryUpdate';
import { BluetoothStateChanged, BluetoothStateChangedSchema } from '@/validation/bluetoothStateChanged';
import { CalibrationDone, CalibrationDoneSchema } from '@/validation/calibrationDone';
import { CalibrationPacket, CalibrationPacketSchema } from '@/validation/calibrationPacket';
import { CalibrationProgress, CalibrationProgressSchema } from '@/validation/calibrationProgress';
import { FileProcessUpdate, FileProcessUpdateSchema } from '@/validation/fileProcessUpdate';
import { HubBatteryUpdate, HubBatteryUpdateSchema } from '@/validation/hubBatteryUpdate';
import { HubInfoUpdate, HubInfoUpdateSchema } from '@/validation/hubInfoUpdate';
import { StreamPacket, StreamPacketSchema } from '@/validation/streamPacket';

import { toast, ToastProps } from '@/components/Toaster/Toast';

import { validateSchema } from '@/utils/validateEvent';

import { DataType, NativeQSenseControl, NativeQSenseControlPlugin } from '@/plugins/NativeQSenseControl';
import { QSenseHubControl, QSenseHubControlPlugin } from '@/plugins/QSenseHubControl';
import { Assignments, KinematicModelOptions } from '@/plugins/QSenseHubControl/types';

import { MovementType } from '@/types/MovementTypes';

import { IAnnotationService } from './interfaces/IAnnotationService';
import { ICalibrationService } from './interfaces/ICalibrationService';
import { IConnectionStrategy } from './interfaces/IConnectionStrategy';
import { IEventDispatcher, SensorData, SensorEventMap } from './interfaces/IEventDispatcher';
import { IFileTransferStrategy } from './interfaces/IFileTransferStrategy';
import { ILedManager } from './interfaces/ILedManager';
import { IListenerRegistry } from './interfaces/IListenerRegistry';
import { IScanStrategy } from './interfaces/IScanStrategy';
import { ISensorsManagement } from './interfaces/ISensorsManagement';
import { ISessionManager } from './interfaces/ISessionManager';
import { IStreamingService } from './interfaces/IStreamingService';
import { ITagManager } from './interfaces/ITagManager';
import { LedManager } from './LedManager';
import { AnnotationService } from './managers/AnnotationService';
import { CalibrationService } from './managers/CalibrationService';
import { EventDispatcher } from './managers/EventDispatcher';
import { ListenerRegistry } from './managers/ListenerRegistry';
import { SensorsManager } from './managers/SensorsManager';
import { SessionManager } from './managers/SessionManager';
import { StreamingService } from './managers/StreamingService';
import { TagManager } from './managers/TagManager';
import { HubConnectionStrategy, HubFileTransferStrategy, HubScanStrategy } from './strategies/HubStrategies';
import { NoHubScanStrategy, NoHubFileTransferStrategy, NoHubConnectionStrategy } from './strategies/NoHubStrategies';

interface CalibrationPacketReceived {
  calibrationPackets: CalibrationPacket[];
}

type SensorUpdateCallback = (sensorId: string, data: SensorData) => void;

function isHubGuard(
  plugin: NativeQSenseControlPlugin | QSenseHubControlPlugin,
  isHub: boolean,
): plugin is QSenseHubControlPlugin {
  return isHub;
}

const CALIBRATION_DURATION = 5500;

/**
 * Manages scanning, connection, and streaming of sensor data from devices.
 * Maintains event listeners and invokes callbacks when sensor updates are received.
 */
class SensorService<TPlugin extends NativeQSenseControlPlugin | QSenseHubControlPlugin> {
  private subscriptionHandlers: Map<SensorUpdateCallback, (payload: { macAddress: string; data: SensorData }) => void> =
    new Map();

  private initializedManagers = false;
  private initializedStrategies = false;
  private initializedListeners = false;

  // Managers
  private tags!: ITagManager;
  private session!: ISessionManager;
  protected events!: IEventDispatcher;
  protected listeners!: IListenerRegistry;
  protected streaming!: IStreamingService;
  protected sensors!: ISensorsManagement;
  protected calibration!: ICalibrationService;
  protected annotations!: IAnnotationService;
  protected led!: ILedManager;

  // Strategies
  protected scan!: IScanStrategy;
  protected file!: IFileTransferStrategy;
  protected connection!: IConnectionStrategy;

  /**
   * @constructor
   * @param {NativeQSenseControlPlugin} plugin - A plugin instance for Q-Sense device interactions.
   */
  constructor(
    private plugin: TPlugin,
    private isHub: boolean,
  ) {
    this.plugin = plugin;

    this.initManagers();
    this.initStrategies();
    this.initListeners();

    // Ensures all init methods have been called
    if (!this.initializedManagers || !this.initializedStrategies || !this.initializedListeners) {
      throw new Error('Failed to initialize sensor service');
    }
  }

  private initManagers(): void {
    this.initializedManagers = true;
    this.tags = new TagManager(this.plugin);
    this.session = new SessionManager(this.plugin);
    this.events = new EventDispatcher();
    this.listeners = new ListenerRegistry(this.plugin);
    this.streaming = new StreamingService(this.plugin);
    this.sensors = new SensorsManager(this.plugin);
    this.calibration = new CalibrationService(this.plugin, this.sensors, this.events);
    this.annotations = new AnnotationService(this.plugin);
    this.led = new LedManager(this.plugin);
  }

  private initStrategies(): void {
    this.initializedStrategies = true;
    if (isHubGuard(this.plugin, this.isHub)) {
      this.scan = new HubScanStrategy();
      this.file = new HubFileTransferStrategy(this.plugin, this.listeners);
      this.connection = new HubConnectionStrategy(this.plugin, this.listeners);
    } else {
      this.scan = new NoHubScanStrategy(this.plugin, this.streaming, this.listeners, this.events);
      this.file = new NoHubFileTransferStrategy();
      this.connection = new NoHubConnectionStrategy(this.plugin);
    }
  }

  private initListeners(): void {
    this.initializedListeners = true;

    // Listen to stream packets from the native layer.
    this.listeners.add<string, StreamPacket>('onStreamPacketReceived', (event) => {
      const result = validateSchema('onStreamPacketReceived', event, StreamPacketSchema);
      if (!result) {
        return;
      }

      const { macAddress, accelerometer } = result;

      this.sensors.updateMovement(macAddress, accelerometer);

      const data = this.sensors.get(macAddress);

      if (!data) {
        return;
      }

      this.events.emit('sensorData', {
        macAddress,
        data,
      });
    });

    this.listeners.add<string, { device: string }>('onDeviceDisconnected', (event) => {
      this.events.emit('deviceDisconnected', {
        macAddress: event.device,
      });
    });

    // Listen to calibration packet events from the native layer.
    this.listeners.add<string, CalibrationPacket | CalibrationPacketReceived>(
      'onCalibrationPacketReceived',
      (event) => {
        if ('calibrationPackets' in event) {
          const { calibrationPackets } = event;

          calibrationPackets.forEach((packet) => {
            const validatedPacket = validateSchema('onCalibrationPacketReceived', packet, CalibrationPacketSchema);
            if (!validatedPacket) {
              return;
            }

            const { device, movement } = validatedPacket;
            this.sensors.updateMovement(device, movement);

            this.events.emit('calibrationPacket', {
              macAddress: device,
              movement,
            });
          });
        } else {
          const result = validateSchema('onCalibrationPacketReceived', event, CalibrationPacketSchema);

          if (!result) {
            return;
          }

          const { device, movement } = result;
          this.sensors.updateMovement(device, movement);

          this.events.emit('calibrationPacket', {
            macAddress: device,
            movement,
          });
        }
      },
    );

    // Listen to battery updates coming from the native layer.
    this.listeners.add<string, BatteryUpdate>('onBatteryUpdate', (event) => {
      const result = validateSchema('onBatteryUpdate', event, BatteryUpdateSchema);
      if (!result) {
        return;
      }

      const { device, battery } = result;

      this.sensors.updateBattery(device, battery);

      this.events.emit('batteryUpdate', {
        macAddress: device,
        battery,
      });
    });

    this.listeners.add<string, HubBatteryUpdate>('onHubBatteryUpdate', (event) => {
      const result = validateSchema('onHubBatteryUpdate', event, HubBatteryUpdateSchema);
      if (!result) {
        return;
      }

      const { batteryLevel } = result;

      this.events.emit('hubBatteryUpdate', {
        battery: batteryLevel,
      });
    });

    this.listeners.add<string, CalibrationProgress>('onCalibrationProgress', (event) => {
      const result = validateSchema('onCalibrationProgress', event, CalibrationProgressSchema);
      if (!result) {
        return;
      }

      const { progress } = result;

      this.events.emit('calibrationProgress', {
        progress,
      });
    });

    this.listeners.add<string, CalibrationDone>('onCalibrationDone', (event) => {
      const result = validateSchema('onCalibrationDone', event, CalibrationDoneSchema);
      if (!result) {
        return;
      }

      const { result: calibrationResult } = result;

      this.events.emit('calibrationDone', {
        result: calibrationResult,
      });
    });

    this.listeners.add('onHubDisconnected', () => {
      this.events.emit('hubDisconnected');
    });

    this.listeners.add<string, BluetoothStateChanged>('onBluetoothStateChanged', (event) => {
      const result = validateSchema('onBluetoothStateChanged', event, BluetoothStateChangedSchema);
      if (!result) {
        return;
      }

      const { usable, state, authorization, message, canRecover, action } = result;

      this.events.emit('bluetoothStateChanged', {
        usable,
        state,
        authorization,
        message,
        canRecover,
        action,
      });
    });

    this.listeners.add<string, HubInfoUpdate>('onHubInfoUpdate', (event) => {
      const result = validateSchema('onHubInfoUpdate', event, HubInfoUpdateSchema);

      if (!result) {
        return;
      }

      const { type, title, message, displayCondition } = result;

      // Show toast message with the hub info
      this.events.emit('hubInfoUpdate', {
        type,
        title,
        message,
        displayCondition,
      });
    });

    this.listeners.add<string, FileProcessUpdate>('onFileProcessUpdate', (event) => {
      const result = validateSchema('onFileProcessUpdate', event, FileProcessUpdateSchema);
      if (!result) {
        return;
      }

      const { variant, title, message, displayCondition, progress } = result;

      this.events.emit('fileProcessUpdate', {
        variant,
        title,
        message,
        displayCondition,
        progress,
      });
    });
  }

  async initialize(
    sessionId: string,
    sessionStartTime: Date,
    config: {
      KinematicModel: KinematicModelOptions;
      Whitelist: string[];
      Assignments: Assignments[];
      NetworkKey: number;
      DeviceState: string;
    },
    clientName: string,
    userId: string,
    idToken: string,
    clientId: string,
  ): Promise<void> {
    await this.session.start(sessionId, sessionStartTime, config, clientName, userId, clientId, idToken);
  }

  async startSession(): Promise<void> {
    if (isHubGuard(this.plugin, this.isHub)) {
      await this.plugin.StartMeasurement();
    }
  }

  async endSession(): Promise<void> {
    if (isHubGuard(this.plugin, this.isHub)) {
      console.log('Stopping measurement (hub)');
      return this.plugin.StopMeasurement();
    }

    await this.session.end();
  }

  async updateTag(measurementType: MovementType, measurementCount: number, tag: string): Promise<void> {
    await this.tags.update(measurementType, measurementCount, tag);
  }

  async deleteTag(measurementType: MovementType, measurementCount: number): Promise<void> {
    await this.tags.delete(measurementType, measurementCount);
  }

  async resetTags(): Promise<void> {
    await this.tags.reset();
  }

  async playAnimation(macAddress: string, red: number, green: number, blue: number, animation: 'blink' | 'fixed') {
    await this.led.playAnimation(macAddress, red, green, blue, animation);
  }

  async removeListener(eventName: string) {
    this.listeners.remove(eventName);
  }

  async startCalibration(count: number, onFinish: () => void): Promise<void> {
    this.calibration.start(count);

    setTimeout(async () => {
      this.stopCalibration(onFinish);
    }, CALIBRATION_DURATION);

    return this.startStreaming();
  }

  async stopCalibration(onFinish: () => void): Promise<void> {
    await this.calibration.stop();
    await this.stopStreaming();
    onFinish();
  }

  async startMeasurement(movementType: MovementType, count: number): Promise<void> {
    await this.annotations.annotate(movementType, count);
  }

  async stopMeasurement(): Promise<void> {
    await this.annotations.annotate(MovementType.idle, 0);
  }

  async stopStreaming(): Promise<void> {
    await this.streaming.stop();
  }

  async startStreaming(): Promise<void> {
    await this.streaming.start();
  }

  async subscribe(sensorId: string, callback: SensorUpdateCallback): Promise<void> {
    // Create a handler that filters events for the given sensorId.
    const handler = (payload: { macAddress: string; data: SensorData }) => {
      if (payload.macAddress === sensorId) {
        callback(payload.macAddress, payload.data);
      }
    };

    // Store the handler for potential unsubscription.
    this.subscriptionHandlers.set(callback, handler);
    this.events.on('sensorData', handler);
  }

  unsubscribe(sensorId: string, callback: SensorUpdateCallback): void {
    const handler = this.subscriptionHandlers.get(callback);

    if (handler) {
      this.events.off('sensorData', handler);
      this.subscriptionHandlers.delete(callback);
    }
  }

  async deleteAnnotation(count: number, measurementType: number): Promise<void> {
    await this.annotations.delete(count, measurementType);
  }

  async restoreAnnotation(count: number, measurementType: number): Promise<void> {
    await this.annotations.restore(count, measurementType);
  }

  async resetDeletedAnnotations(): Promise<void> {
    await this.annotations.reset();
  }

  async startScan(allowedDevices: string[]): Promise<{ status: string }> {
    return this.scan.start(allowedDevices);
  }

  async stopScan(): Promise<{ status: string }> {
    return this.scan.stop();
  }

  async uploadFile(callback: VoidFunction): Promise<void> {
    return this.file.upload(callback);
  }

  async downloadFile(callback: VoidFunction): Promise<void> {
    return this.file.download(callback);
  }

  async connectDevices(callback: VoidFunction) {
    return this.connection.connect(callback);
  }

  async disconnectDevices() {
    return this.connection.disconnect();
  }

  async getSensorData(sensorId: string, dataType: DataType): Promise<{ data: any }> {
    return this.sensors.getData(sensorId, dataType);
  }

  async startOffsetCompensation(): Promise<{ status: string }> {
    if (isHubGuard(this.plugin, this.isHub)) {
      return this.plugin.StartOffsetCompensation();
    }
    throw new Error('Offset compensation is only available for Hub devices');
  }

  destroy(): void {
    this.listeners.removeAll();

    for (const [callback, handler] of this.subscriptionHandlers.entries()) {
      this.events.off('sensorData', handler);
      this.subscriptionHandlers.delete(callback);
    }
  }

  emit(event: keyof SensorEventMap, payload?: SensorEventMap[string]) {
    this.events.emit(event, payload);
  }

  on<Key extends keyof SensorEventMap>(event: Key, callback: (payload: SensorEventMap[Key]) => void) {
    this.events.on(event, callback);
  }

  off<Key extends keyof SensorEventMap>(event: Key, callback: (payload: SensorEventMap[Key]) => void) {
    this.events.off(event, callback);
  }
}

export const sensorService = new SensorService(NativeQSenseControl, false);
export const hubSensorService = new SensorService(QSenseHubControl, true);

// Hot module replacement cleanup
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    sensorService.destroy();
    hubSensorService.destroy();
  });
}
